import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import {
  Card,
  Button,
  Table,
  Badge,
  Form,
  Spinner,
  Modal,
  Pagination
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaToggleOn, FaToggleOff, FaTrash, FaUserMd } from 'react-icons/fa';

const Doctors = () => {
  const { user } = useContext(AuthContext);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [doctorToDelete, setDoctorToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Get permissions
  const permissions = user?.role === 'admin' ? {} : (user?.permissions || {});
  const isAdmin = user?.role === 'admin';
  const hasPermission = (key) => isAdmin || permissions[key] === true;

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const res = await axios.get('/api/doctors.php');
        setDoctors(res.data);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };

    fetchDoctors();
  }, []);

  const toggleDoctorStatus = async (id, currentStatus) => {
    try {
      // Get current doctor data
      const doctor = doctors.find(d => d.id === id);
      
      await axios.put(`/api/doctors.php?id=${id}`, {
        ...doctor,
        isActive: currentStatus ? 0 : 1,
        id: id
      });

      setDoctors(
        doctors.map(doctor =>
          doctor.id === id ? { ...doctor, isActive: !currentStatus } : doctor
        )
      );
    } catch (err) {
      console.error(err);
    }
  };

  // Handle delete doctor
  const handleDeleteClick = (doctor) => {
    setDoctorToDelete(doctor);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!doctorToDelete) return;
    
    try {
      await axios.delete(`/api/doctors.php?id=${doctorToDelete.id}`);
      
      // Remove doctor from state
      setDoctors(doctors.filter(d => d.id !== doctorToDelete.id));
      setShowDeleteModal(false);
      setDoctorToDelete(null);
    } catch (err) {
      console.error(err);
      // Handle error (could add error state and display message)
    }
  };



  const filteredDoctors = showInactive
    ? doctors
    : doctors.filter(doctor => doctor.isActive);

  // Pagination
  const totalPages = Math.ceil(filteredDoctors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDoctors = filteredDoctors.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [showInactive]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Doctors</h2>
        {hasPermission('addDoctor') && (
          <Link to="/doctors/add">
            <Button variant="primary">
              <FaPlus className="me-2" /> Add New Doctor
            </Button>
          </Link>
        )}
      </div>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Doctors List</h5>
          <Form.Check
            type="switch"
            id="show-inactive"
            label="Show Inactive Doctors"
            checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
          />
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : filteredDoctors.length > 0 ? (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Specialization</th>
                  <th>Qualification</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentDoctors.map(doctor => (
                  <tr key={doctor.id}>
                    <td>{doctor.name}</td>
                    <td>{doctor.specialization}</td>
                    <td>{doctor.qualification}</td>
                    <td>{doctor.contactNumber}</td>
                    <td>
                      {doctor.isActive ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="secondary">Inactive</Badge>
                      )}
                    </td>
                    <td>
                      {hasPermission('viewDoctor') && (
                        <Link to={`/doctor-portal/${doctor.id}`} className="btn btn-sm btn-info me-2">
                          <FaUserMd />
                        </Link>
                      )}
                      {hasPermission('editDoctor') && (
                        <Link to={`/doctors/edit/${doctor.id}`} className="btn btn-sm btn-warning me-2">
                          <FaEdit />
                        </Link>
                      )}
                      {hasPermission('toggleDoctor') && (
                        <Button
                          variant={doctor.isActive ? 'danger' : 'success'}
                          size="sm"
                          className="me-2"
                          onClick={() => toggleDoctorStatus(doctor.id, doctor.isActive)}
                        >
                          {doctor.isActive ? <FaToggleOff /> : <FaToggleOn />}
                        </Button>
                      )}
                      {hasPermission('deleteDoctor') && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteClick(doctor)}
                        >
                          <FaTrash />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center my-5">
              <p>No doctors found. Please add a new doctor.</p>
              <Link to="/doctors/add">
                <Button variant="primary">
                  <FaPlus className="me-2" /> Add New Doctor
                </Button>
              </Link>
            </div>
          )}
          
          {/* Pagination */}
          {filteredDoctors.length > itemsPerPage && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                
                {[...Array(totalPages)].map((_, index) => (
                  <Pagination.Item
                    key={index + 1}
                    active={index + 1 === currentPage}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </Pagination.Item>
                ))}
                
                <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete Dr. <strong>{doctorToDelete?.name}</strong>?
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete Doctor
          </Button>
        </Modal.Footer>
      </Modal>


    </div>
  );
};

export default Doctors; 
